import { useEffect, useState } from "react";
import { Button, Input } from "@heroui/react";
import {
  IconArrowLeft,
  IconDiscount2,
  IconSquareX,
  IconCreditCard,
  IconDeviceMobile,
  IconMapPin,
  IconBuilding,
} from "@tabler/icons-react";
import { useTranslations } from "../../../../i18n";
import { api, ApiError } from "../../../lib/api";
import {
  fetchPaymentSettings,
  parseBankDetails,
  getTwintPhoneNumber,
  getTwintQrCode,
  type PaymentSettings,
} from "../../../lib/paymentApi";
import TwintQrCode from "../../twint-qr-code";
import {
  formatAutoBookSlot,
  type PackagePass,
} from "../../../hooks/use-studio-packages";
import { getWidgetLocale } from "../../../lib/widget-locale";

// Le checkout de pass n'implemente que ces quatre methodes cote serveur
// (packages.ts) : PayPal n'a pas d'integration directe pour les passes.
export type PassPaymentMethod = "CARD" | "TWINT" | "CASH" | "TRANSFER";

/**
 * Etape de confirmation avant le paiement d'un pass.
 *
 * Elle n'existait pas : le bouton « Acheter » envoyait directement sur Stripe,
 * sans aucun endroit ou saisir un code promo. C'est ce qui faisait dire aux
 * clientes d'Eva qu'elles ne trouvaient pas ou utiliser leur code.
 *
 * Le montant affiche ici est purement indicatif : c'est le serveur qui
 * revalide le code et recalcule le prix au moment de creer la session Stripe.
 */

type Props = {
  pass: PackagePass;
  studioId: string;
  formatPrice: (price: number) => string;
  isCheckingOut: boolean;
  onBack: () => void;
  onConfirm: (
    applied?: AppliedCode,
    paymentMethod?: PassPaymentMethod,
    autoBookCourseId?: string,
  ) => void;
};

export type AppliedCode = {
  code: string;
  description: string | null;
  discountAmount: number;
  newAmount: number;
  // Un code promo et une carte cadeau se saisissent au meme endroit mais ne
  // partent pas dans le meme champ : le serveur cherche l'un dans
  // `discount_codes` et l'autre dans `gift_cards`. Les confondre, c'est ce
  // qui faisait echouer la reservation apres avoir affiche la reduction.
  type: "DISCOUNT_CODE" | "GIFT_CARD";
  remainingAfter: number | null;
};

// Meme ordre de priorite que le selecteur de la reservation de cours
// (payment-methods.tsx) : carte en premier quand elle est disponible.
function getAvailableDirectMethods(
  settings: PaymentSettings,
): PassPaymentMethod[] {
  const methods: PassPaymentMethod[] = [];
  if (settings.onlinePayments) methods.push("CARD");
  if (settings.twint) methods.push("TWINT");
  if (settings.payLater) methods.push("CASH");
  if (settings.bankTransfer) methods.push("TRANSFER");
  return methods;
}

export default function PassPromoStep({
  pass,
  studioId,
  formatPrice,
  isCheckingOut,
  onBack,
  onConfirm,
}: Props) {
  const t = useTranslations("packages");
  const tc = useTranslations("checkout");

  const [input, setInput] = useState("");
  const [applied, setApplied] = useState<AppliedCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings | null>(
    null,
  );
  const [isLoadingPaymentSettings, setIsLoadingPaymentSettings] =
    useState(true);
  const [selectedMethod, setSelectedMethod] =
    useState<PassPaymentMethod | null>(null);

  // Pass a reservation automatique : une seule classe s'impose, plusieurs se
  // choisissent ici (le serveur refuse l'achat sans choix).
  const autoBookCourses = pass.autoBookCourses ?? [];
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(
    autoBookCourses.length === 1 ? autoBookCourses[0].id : null,
  );
  const selectedCourse = autoBookCourses.find(
    (course) => course.id === selectedCourseId,
  );

  // Un studio sans Stripe peut quand meme vendre un pass en TWINT, especes ou
  // virement : sans ce chargement, seul Stripe etait propose et l'achat
  // echouait avec « Stripe not connected » des que le prix n'etait pas nul.
  useEffect(() => {
    let cancelled = false;

    const loadPaymentSettings = async () => {
      setIsLoadingPaymentSettings(true);
      const settings = await fetchPaymentSettings();
      if (cancelled) return;

      setPaymentSettings(settings);

      const availableMethods = getAvailableDirectMethods(settings);
      setSelectedMethod(availableMethods[0] ?? null);
      setIsLoadingPaymentSettings(false);
    };

    loadPaymentSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  const messageForCode = (code?: string) => {
    switch (code) {
      case "NOT_APPLICABLE_HERE":
        return t("promoNotApplicableToPasses");
      case "USAGE_LIMIT_REACHED":
        return t("promoUsageLimitReached");
      case "MIN_PURCHASE_NOT_MET":
        return t("promoMinPurchaseNotMet");
      case "GIFT_CARD_EMPTY":
        return t("giftCardEmpty");
      case "GIFT_CARD_WRONG_CURRENCY":
        return t("giftCardWrongCurrency");
      default:
        return tc("invalidPromoCode");
    }
  };

  const handleApply = async () => {
    const code = input.trim();
    if (!code) return;

    setIsValidating(true);
    setError(null);

    try {
      const data = await api("/api/widget/promo/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          amount: pass.price,
          studioId,
          appliesTo: "PASS",
        }),
      });

      setApplied({
        code: data.code,
        description: data.description ?? null,
        discountAmount: Number(data.discountAmount),
        newAmount: Number(data.newAmount),
        type: data.type === "GIFT_CARD" ? "GIFT_CARD" : "DISCOUNT_CODE",
        remainingAfter:
          data.remainingAfter != null ? Number(data.remainingAfter) : null,
      });
      setInput("");
    } catch (err) {
      setApplied(null);
      setError(
        messageForCode(
          err instanceof ApiError
            ? ((err.body as { error?: string })?.error ?? err.code)
            : undefined,
        ),
      );
    } finally {
      setIsValidating(false);
    }
  };

  const total = applied ? applied.newAmount : pass.price;
  const availableMethods = paymentSettings
    ? getAvailableDirectMethods(paymentSettings)
    : [];
  const requiresPaymentMethod = total > 0;
  const noMethodAvailable =
    requiresPaymentMethod &&
    !isLoadingPaymentSettings &&
    availableMethods.length === 0;
  const canConfirm =
    (autoBookCourses.length === 0 || !!selectedCourseId) &&
    (!requiresPaymentMethod ||
      (!isLoadingPaymentSettings && !!selectedMethod));

  return (
    <div className="w-full p-4">
      <div className="max-w-md mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <IconArrowLeft size={16} />
          {t("back")}
        </button>

        <div className="rounded-xl border border-default-200 p-5 space-y-4">
          <div>
            <h3 className="font-semibold text-foreground">{pass.title}</h3>
            {pass.description && (
              <p className="text-sm text-muted-foreground mt-1">
                {pass.description}
              </p>
            )}
          </div>

          {autoBookCourses.length > 1 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">
                {t("autoBookChooseClass")}
              </p>
              <div className="grid gap-2">
                {autoBookCourses.map((course) => {
                  const isSelected = course.id === selectedCourseId;
                  return (
                    <button
                      key={course.id}
                      type="button"
                      onClick={() => setSelectedCourseId(course.id)}
                      className={`cursor-pointer flex items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                        isSelected
                          ? "border-primary bg-primary/5 text-foreground"
                          : "border-default-200 text-muted-foreground hover:border-primary/50"
                      }`}
                    >
                      <span className="font-medium">{course.title}</span>
                      <span className="capitalize">
                        {formatAutoBookSlot(course, getWidgetLocale() ?? undefined)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {selectedCourse && (
            <p className="text-sm text-muted-foreground">
              {t("autoBookInfo", {
                course: `${selectedCourse.title} (${formatAutoBookSlot(
                  selectedCourse,
                  getWidgetLocale() ?? undefined,
                )})`,
              })}
            </p>
          )}

          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t("price")}</span>
            <span>{formatPrice(pass.price)}</span>
          </div>

          {applied ? (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2">
              <div className="flex items-center gap-2 text-green-700 min-w-0">
                <IconDiscount2 size={16} />
                <span className="text-sm font-medium truncate">
                  {applied.code}
                </span>
                {applied.type === "GIFT_CARD" &&
                  applied.remainingAfter !== null && (
                    <span className="text-xs text-green-600 shrink-0">
                      {t("giftCardRemainingAfter", {
                        amount: formatPrice(applied.remainingAfter),
                      })}
                    </span>
                  )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-sm font-semibold text-green-700">
                  −{formatPrice(applied.discountAmount)}
                </span>
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  isIconOnly
                  onPress={() => {
                    setApplied(null);
                    setError(null);
                  }}
                >
                  <IconSquareX size={14} />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                placeholder={tc("promoCode")}
                value={input}
                onValueChange={(v) => {
                  setInput(v);
                  if (error) setError(null);
                }}
                size="sm"
                variant="bordered"
                className="flex-1"
                onKeyDown={(e: React.KeyboardEvent) =>
                  e.key === "Enter" && handleApply()
                }
                isInvalid={!!error}
                errorMessage={error ?? undefined}
              />
              <Button
                size="sm"
                variant="flat"
                color="primary"
                onPress={handleApply}
                isLoading={isValidating}
                isDisabled={!input.trim()}
              >
                {tc("apply")}
              </Button>
            </div>
          )}

          <div className="flex justify-between items-center pt-2 border-t border-default-200">
            <span className="font-medium">{tc("overall")}</span>
            <div className="text-right">
              {applied && (
                <div className="text-sm text-gray-400 line-through">
                  {formatPrice(pass.price)}
                </div>
              )}
              <div className="font-semibold text-xl">{formatPrice(total)}</div>
            </div>
          </div>

          {requiresPaymentMethod && (
            <PaymentMethodPicker
              isLoading={isLoadingPaymentSettings}
              methods={availableMethods}
              selected={selectedMethod}
              onSelect={setSelectedMethod}
              paymentSettings={paymentSettings}
              tc={tc}
              noMethodAvailable={noMethodAvailable}
            />
          )}

          <Button
            color="primary"
            className="w-full h-12"
            isLoading={isCheckingOut}
            isDisabled={!canConfirm}
            onPress={() =>
              onConfirm(
                applied ?? undefined,
                selectedMethod ?? undefined,
                selectedCourseId ?? undefined,
              )
            }
          >
            {total <= 0 ? t("confirmFreePurchase") : t("proceedToPayment")}
          </Button>
        </div>
      </div>
    </div>
  );
}

const METHOD_ICON: Record<PassPaymentMethod, typeof IconCreditCard> = {
  CARD: IconCreditCard,
  TWINT: IconDeviceMobile,
  CASH: IconMapPin,
  TRANSFER: IconBuilding,
};

function methodLabel(
  method: PassPaymentMethod,
  tc: (key: string, opts?: any) => string,
): string {
  switch (method) {
    case "CARD":
      return tc("payWithCard") || "Pay with credit or debit card";
    case "TWINT":
      return "TWINT";
    case "CASH":
      return tc("payWhenYouArrive") || "Pay when you arrive";
    case "TRANSFER":
      return tc("transferToBankAccount") || "Transfer to bank account";
  }
}

function PaymentMethodPicker({
  isLoading,
  methods,
  selected,
  onSelect,
  paymentSettings,
  tc,
  noMethodAvailable,
}: {
  isLoading: boolean;
  methods: PassPaymentMethod[];
  selected: PassPaymentMethod | null;
  onSelect: (method: PassPaymentMethod) => void;
  paymentSettings: PaymentSettings | null;
  tc: (key: string, opts?: any) => string;
  noMethodAvailable: boolean;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
        {tc("loadingPaymentOptions") || "Loading payment options..."}
      </div>
    );
  }

  if (noMethodAvailable) {
    return (
      <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
        {tc("cardPaymentsUnavailable") ||
          "Card payments are not available for this studio. Please contact them directly."}
      </p>
    );
  }

  const bankDetails = paymentSettings ? parseBankDetails(paymentSettings) : null;
  const twintPhoneNumber = paymentSettings
    ? getTwintPhoneNumber(paymentSettings)
    : null;
  const twintQrCode = paymentSettings ? getTwintQrCode(paymentSettings) : null;

  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-foreground">
        {tc("selectPaymentMethod") || "Select payment method"}
      </span>

      <div className="space-y-2">
        {methods.map((method) => {
          const Icon = METHOD_ICON[method];
          const isSelected = selected === method;

          return (
            <div
              key={method}
              className={`rounded-lg border transition-colors ${
                isSelected
                  ? "border-primary ring-1 ring-primary/30"
                  : "border-default-200"
              }`}
            >
              <button
                type="button"
                onClick={() => onSelect(method)}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                    isSelected ? "border-primary" : "border-default-300"
                  }`}
                >
                  {isSelected && (
                    <span className="h-2 w-2 rounded-full bg-primary" />
                  )}
                </span>
                <Icon size={18} className="text-gray-600 shrink-0" />
                <span className="text-sm">{methodLabel(method, tc)}</span>
              </button>

              {isSelected && method === "TWINT" && (
                <div className="px-3 pb-3 text-sm text-muted-foreground space-y-2">
                  {twintQrCode && (
                    <div className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-md">
                      <TwintQrCode
                        qrCodeKey={twintQrCode}
                        imageClassName="w-28 h-28"
                      />
                    </div>
                  )}
                  {!twintQrCode && twintPhoneNumber && (
                    <p>
                      {(tc("phoneNumber") || "Phone number") +
                        ": " +
                        twintPhoneNumber}
                    </p>
                  )}
                </div>
              )}

              {isSelected && method === "TRANSFER" && bankDetails && (
                <div className="px-3 pb-3 text-sm text-muted-foreground space-y-1">
                  <p>
                    {(tc("accountName") || "Account name") +
                      ": " +
                      bankDetails.accountName}
                  </p>
                  <p>
                    {(tc("accountNumber") || "Account number") +
                      ": " +
                      bankDetails.accountNumber}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
